/**
 * StorageService - Single source of truth for all storage operations
 * 
 * **THE CORE PRINCIPLE:**
 * App code NEVER knows if storage is local or cloud.
 * All reads/writes go through this unified interface.
 * 
 * Backend selection is automatic based on auth state:
 * - Logged OUT → LocalStorageBackend
 * - Logged IN → CloudStorageBackend
 * 
 * The ONLY exception is the login flow, which handles migration
 * from local to cloud (existing popup UI, just wired up here).
 */

import { createLocalStorageBackend } from './backends/LocalStorageBackend.js';
import { createCloudStorageBackend } from './backends/CloudStorageBackend.js';
import { AUTOSAVE_DEBOUNCE_MS } from '../constants/index.js';
import { createPatchData } from '../utils/serialization.js';

/**
 * Creates a StorageService instance
 * 
 * @param {Object} deps - Dependencies
 * @param {Object} deps.authService - AuthService instance
 * @param {Object} deps.definitionModel - Device definition model
 * @param {Object} deps.instanceModel - Device instance model
 * @param {Object} deps.connectionModel - Connection model
 * @param {Function} [deps.getPatchSettings] - Returns current patch settings or null
 * @returns {Object} StorageService instance
 */
export function createStorageService(deps) {
    const {
        authService,
        definitionModel,
        instanceModel,
        connectionModel,
        getPatchSettings
    } = deps;
    
    // ============= BACKENDS =============
    const localBackend = createLocalStorageBackend();
    const cloudBackend = createCloudStorageBackend(authService);
    
    // ============= INTERNAL STATE =============
    let currentPatchId = null;
    let currentPatchName = null;
    let autosaveTimer = null;
    let autosaveCallback = null;
    
    // ============= BACKEND SELECTION =============
    
    function isCloud() {
        return authService?.isLoggedIn() ?? false;
    }
    
    function getBackend() {
        return isCloud() ? cloudBackend : localBackend;
    }
    
    // ============= PATCH ID MANAGEMENT =============
    
    function getCurrentPatchId() {
        return currentPatchId;
    }
    
    function setCurrentPatchId(id) {
        currentPatchId = id;
    }
    
    function clearCurrentPatchId() {
        currentPatchId = null;
    }
    
    function getCurrentPatchName() {
        return currentPatchName;
    }
    
    function setCurrentPatchName(name) {
        currentPatchName = name;
    }
    
    // ============= PATCH DATA CREATION =============
    
    function createCurrentPatchData() {
        return createPatchData({
            templates: definitionModel.getAll(),
            devices: instanceModel.getAll(),
            connections: connectionModel.getAll(),
            settings: getPatchSettings ? getPatchSettings() : null
        });
    }
    
    // ============= WORKING PATCH (AUTOSAVE) =============
    
    async function saveWorkingPatch(patchData = null) {
        const data = patchData || createCurrentPatchData();
        const backend = getBackend();
        return await backend.saveWorkingPatch(data);
    }
    
    async function loadWorkingPatch() {
        const backend = getBackend();
        return await backend.loadWorkingPatch();
    }
    
    async function clearWorkingPatch() {
        const backend = getBackend();
        return await backend.clearWorkingPatch();
    }
    
    // ============= AUTOSAVE =============
    
    function setAutosaveCallback(callback) {
        autosaveCallback = callback;
    }
    
    function scheduleAutosave() {
        if (autosaveTimer) {
            clearTimeout(autosaveTimer);
        }
        autosaveTimer = setTimeout(async () => {
            await performAutosave();
            autosaveTimer = null;
        }, AUTOSAVE_DEBOUNCE_MS);
    }
    
    async function triggerImmediateAutosave() {
        if (autosaveTimer) {
            clearTimeout(autosaveTimer);
            autosaveTimer = null;
        }
        await performAutosave();
    }
    
    async function performAutosave() {
        // Always save to working patch (quick recovery)
        const result = await saveWorkingPatch();
        
        // If a named patch is currently loaded, also update it
        // This ensures changes persist when switching between patches
        if (result.success && currentPatchId && currentPatchName) {
            const patchData = createCurrentPatchData();
            const deviceCount = instanceModel.getCount();
            const connectionCount = connectionModel.getCount();
            const backend = getBackend();
            
            await backend.savePatch(
                currentPatchName,
                patchData,
                deviceCount,
                connectionCount,
                false, // Don't force insert - update existing
                isCloud() ? currentPatchId : null
            );
        }
        
        if (result.success && autosaveCallback) {
            autosaveCallback();
        }
        return result;
    }
    
    // ============= NAMED PATCHES =============
    
    async function savePatch(name, forceInsert = false) {
        const patchData = createCurrentPatchData();
        const deviceCount = instanceModel.getCount();
        const connectionCount = connectionModel.getCount();
        const backend = getBackend();
        
        const result = await backend.savePatch(
            name,
            patchData,
            deviceCount,
            connectionCount,
            forceInsert,
            isCloud() ? currentPatchId : null
        );
        
        if (result.success && result.id) {
            currentPatchId = result.id;
            currentPatchName = result.name || name;
        }
        
        return result;
    }
    
    async function loadPatch(identifier) {
        const backend = getBackend();
        const result = await backend.loadPatch(identifier);
        
        if (result.success && result.id) {
            currentPatchId = result.id;
            currentPatchName = result.name;
        }
        
        return result;
    }
    
    async function deletePatch(identifier) {
        const backend = getBackend();
        return await backend.deletePatch(identifier);
    }
    
    async function listPatches() {
        const backend = getBackend();
        return await backend.listPatches();
    }
    
    // ============= VERSIONS =============
    
    async function saveVersion(name) {
        const patchData = createCurrentPatchData();
        const deviceCount = instanceModel.getCount();
        const connectionCount = connectionModel.getCount();
        const backend = getBackend();
        
        return await backend.saveVersion(name, patchData, deviceCount, connectionCount);
    }
    
    async function loadVersion(versionId) {
        const backend = getBackend();
        return await backend.loadVersion(versionId);
    }
    
    async function deleteVersion(versionId) {
        const backend = getBackend();
        return await backend.deleteVersion(versionId);
    }
    
    async function listVersions() {
        const backend = getBackend();
        return await backend.listVersions();
    }
    
    async function renameVersion(versionId, newName) {
        const backend = getBackend();
        return await backend.renameVersion(versionId, newName);
    }
    
    // ============= AUTH STATE CHANGES =============
    
    async function hasLocalDataToMigrate() {
        return await localBackend.hasDataToMigrate();
    }
    
    async function getLocalDataForMigration() {
        return await localBackend.getDataForMigration();
    }
    
    async function migrateLocalToCloud(mode) {
        if (mode === 'skip') {
            return { success: true, migratedCount: 0, errors: [] };
        }
        
        if (!isCloud()) {
            return { success: false, migratedCount: 0, errors: ['Not logged in'] };
        }
        
        const localData = await localBackend.getDataForMigration();
        const errors = [];
        let migratedCount = 0;
        
        if (localData.workingPatch) {
            const result = await cloudBackend.saveWorkingPatch(localData.workingPatch);
            if (result.success) {
                migratedCount++;
            } else {
                errors.push(`Working patch: ${result.error}`);
            }
        }
        
        for (const patch of localData.patches) {
            const result = await cloudBackend.savePatch(
                patch.name,
                patch.data,
                patch.deviceCount || 0,
                patch.connectionCount || 0,
                true
            );
            if (result.success) {
                migratedCount++;
            } else {
                errors.push(`Patch "${patch.name}": ${result.error}`);
            }
        }
        
        for (const version of localData.versions) {
            const result = await cloudBackend.saveVersion(
                version.name,
                version.data,
                version.deviceCount || 0,
                version.connectionCount || 0
            );
            if (result.success) {
                migratedCount++;
            } else {
                errors.push(`Version "${version.name}": ${result.error}`);
            }
        }
        
        if (mode === 'move' && migratedCount > 0) {
            await localBackend.clearAll();
        }
        
        return {
            success: errors.length === 0,
            migratedCount,
            errors
        };
    }
    
    async function onLogout() {
        currentPatchId = null;
        currentPatchName = null;
        
        const localData = await localBackend.loadWorkingPatch();
        if (localData.success) {
            return localData;
        }
        
        return { success: false, data: null };
    }
    
    // ============= PUBLIC API =============
    
    return {
        isCloud,
        
        getCurrentPatchId,
        setCurrentPatchId,
        clearCurrentPatchId,
        getCurrentPatchName,
        setCurrentPatchName,
        
        createCurrentPatchData,
        
        saveWorkingPatch,
        loadWorkingPatch,
        clearWorkingPatch,
        
        setAutosaveCallback,
        scheduleAutosave,
        triggerImmediateAutosave,
        
        savePatch,
        loadPatch,
        deletePatch,
        listPatches,
        
        saveVersion,
        loadVersion,
        deleteVersion,
        listVersions,
        renameVersion,
        
        hasLocalDataToMigrate,
        getLocalDataForMigration,
        migrateLocalToCloud,
        onLogout,
        
        invalidateCloudCache: () => {}
    };
}
